import {
  MCP_READ_SCOPE,
  MCP_SCOPE_CATEGORY_COPY,
  MCP_SCOPE_DEFINITIONS,
  DASHBOARD_MCP_RESOURCE_SCOPES,
  type McpScopeCategory,
  type McpScopeRisk,
} from "@arti/schemas/mcpScopes";
import { assertUnreachable, hasValue } from "@arti/utilities";

export type DashboardMcpScopeCategory = Exclude<
  McpScopeCategory,
  "identity" | "skills"
>;
export type DashboardMcpScopeRisk = McpScopeRisk;

export interface DashboardMcpScopeOption {
  category: DashboardMcpScopeCategory;
  description: string;
  label: string;
  risk: DashboardMcpScopeRisk;
  scope: string;
}

export const DASHBOARD_MCP_SCOPE_CATEGORY_ORDER: readonly DashboardMcpScopeCategory[] =
  ["metadata", "indexing", "delivery", "actors", "channel_environments"];

export const DASHBOARD_MCP_SCOPE_CATEGORY_COPY = {
  actors: MCP_SCOPE_CATEGORY_COPY.actors,
  channel_environments: MCP_SCOPE_CATEGORY_COPY.channel_environments,
  delivery: MCP_SCOPE_CATEGORY_COPY.delivery,
  indexing: MCP_SCOPE_CATEGORY_COPY.indexing,
  metadata: MCP_SCOPE_CATEGORY_COPY.metadata,
} satisfies Record<
  DashboardMcpScopeCategory,
  { description: string; title: string }
>;

export const DASHBOARD_MCP_OPTIONAL_SCOPE_OPTIONS: readonly DashboardMcpScopeOption[] =
  MCP_SCOPE_DEFINITIONS.flatMap((definition) => {
    if (
      !definition.optional ||
      definition.category === "identity" ||
      definition.category === "skills"
    ) {
      return [];
    }

    return [
      {
        category: definition.category,
        description: definition.description,
        label: definition.label,
        risk: definition.risk,
        scope: definition.scope,
      } satisfies DashboardMcpScopeOption,
    ];
  });

export function normalizeDashboardMcpTokenScopes(
  scopes: readonly string[],
): string[] {
  const selected = new Set([MCP_READ_SCOPE, ...scopes]);
  return DASHBOARD_MCP_RESOURCE_SCOPES.filter((scope) => selected.has(scope));
}

export function getDashboardMcpOptionsForCategory(
  category: DashboardMcpScopeCategory,
): DashboardMcpScopeOption[] {
  return DASHBOARD_MCP_OPTIONAL_SCOPE_OPTIONS.filter(
    (option) => option.category === category,
  );
}

export type DashboardMcpAccessProfileTone =
  | "baseline"
  | "elevated"
  | "recovery";

export interface DashboardMcpAccessProfile {
  description: string;
  label: string;
  tone: DashboardMcpAccessProfileTone;
}

const DASHBOARD_MCP_ACCESS_PROFILES = {
  baseline: {
    description:
      "Observational access only. Suitable for copilots that need context without changing delivery state.",
    label: "Observation Access",
    tone: "baseline",
  },
  elevated: {
    description:
      "Includes guarded write actions such as indexing refreshes or delivery interventions, in addition to read access.",
    label: "Intervention Access",
    tone: "elevated",
  },
  recovery: {
    description:
      "Includes force-style recovery operations. Keep this for tightly controlled operator workflows only.",
    label: "Recovery Access",
    tone: "recovery",
  },
} satisfies Record<DashboardMcpAccessProfileTone, DashboardMcpAccessProfile>;

/** Lowest to highest: a scope set reads as the strongest tone it carries. */
const DASHBOARD_MCP_ACCESS_TONE_PRECEDENCE: readonly DashboardMcpAccessProfileTone[] =
  ["baseline", "elevated", "recovery"];

const DASHBOARD_MCP_SCOPE_RISKS: ReadonlyMap<string, McpScopeRisk> = new Map(
  MCP_SCOPE_DEFINITIONS.map((definition) => [
    definition.scope,
    definition.risk,
  ]),
);

function getToneForRisk(risk: McpScopeRisk): DashboardMcpAccessProfileTone {
  switch (risk) {
    case "force": {
      return "recovery";
    }
    case "read": {
      return "baseline";
    }
    case "write": {
      return "elevated";
    }
    default: {
      throw assertUnreachable(risk);
    }
  }
}

/**
 * Derived from each scope's declared `risk`, never from a hard-coded scope list,
 * so a new write scope reads as an elevated permission the day it is added.
 */
export function getDashboardMcpAccessProfile(
  scopes: readonly string[],
): DashboardMcpAccessProfile {
  const tone = normalizeDashboardMcpTokenScopes(
    scopes,
  ).reduce<DashboardMcpAccessProfileTone>((highest, scope) => {
    const risk = DASHBOARD_MCP_SCOPE_RISKS.get(scope);
    if (!hasValue(risk)) {
      return highest;
    }
    const candidate = getToneForRisk(risk);
    return DASHBOARD_MCP_ACCESS_TONE_PRECEDENCE.indexOf(candidate) >
      DASHBOARD_MCP_ACCESS_TONE_PRECEDENCE.indexOf(highest)
      ? candidate
      : highest;
  }, "baseline");
  return DASHBOARD_MCP_ACCESS_PROFILES[tone];
}
